import type {
  BackupManifest,
  BackupPackage,
  RestoreHistoryEntry,
  RetentionPolicy
} from "../../../domain/backup/backup-models.js";
import { DEFAULT_RETENTION_POLICY } from "../../../domain/backup/backup-models.js";
import type { BackupRepository } from "../../../repositories/backup-repository.js";

const BACKUP_PACKAGES_STORAGE_KEY = "inspect_backup_packages";
const BACKUP_RETENTION_STORAGE_KEY = "inspect_backup_retention_policy";
const RESTORE_HISTORY_STORAGE_KEY = "inspect_restore_history";

export class InMemoryBackupRepository implements BackupRepository {
  private packages = new Map<string, BackupPackage>();
  private retentionPolicy: RetentionPolicy = { ...DEFAULT_RETENTION_POLICY };
  private restoreHistory: RestoreHistoryEntry[] = [];

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      if (typeof localStorage !== "undefined") {
        const rawPackages = localStorage.getItem(BACKUP_PACKAGES_STORAGE_KEY);
        if (rawPackages) {
          const parsed = JSON.parse(rawPackages) as Array<[string, BackupPackage]>;
          this.packages = new Map(parsed);
        }

        const rawRetention = localStorage.getItem(BACKUP_RETENTION_STORAGE_KEY);
        if (rawRetention) {
          this.retentionPolicy = JSON.parse(rawRetention);
        }

        const rawHistory = localStorage.getItem(RESTORE_HISTORY_STORAGE_KEY);
        if (rawHistory) {
          this.restoreHistory = JSON.parse(rawHistory);
        }
      }
    } catch (error) {
      console.warn("Failed to load backup data from localStorage", error);
    }
  }

  private persistPackages(): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          BACKUP_PACKAGES_STORAGE_KEY,
          JSON.stringify(Array.from(this.packages.entries()))
        );
      }
    } catch (error) {
      console.warn("Failed to persist backup packages to localStorage", error);
    }
  }

  private persistRetention(): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          BACKUP_RETENTION_STORAGE_KEY,
          JSON.stringify(this.retentionPolicy)
        );
      }
    } catch (error) {
      console.warn("Failed to persist retention policy to localStorage", error);
    }
  }

  private persistHistory(): void {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          RESTORE_HISTORY_STORAGE_KEY,
          JSON.stringify(this.restoreHistory)
        );
      }
    } catch (error) {
      console.warn("Failed to persist restore history to localStorage", error);
    }
  }

  async listBackups(clientId?: string): Promise<readonly BackupManifest[]> {
    return Array.from(this.packages.values())
      .map(p => p.manifest)
      .filter(m => !clientId || !m.clientId || m.clientId === clientId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async getBackupPackage(id: string): Promise<BackupPackage | null> {
    const pkg = this.packages.get(id);
    if (!pkg) return null;
    return JSON.parse(JSON.stringify(pkg)) as BackupPackage;
  }

  async saveBackupPackage(pkg: BackupPackage): Promise<void> {
    const cloned = JSON.parse(JSON.stringify(pkg)) as BackupPackage;
    this.packages.set(cloned.manifest.id, cloned);
    this.persistPackages();
  }

  async deleteBackup(id: string): Promise<boolean> {
    const existed = this.packages.delete(id);
    if (existed) {
      this.persistPackages();
    }
    return existed;
  }

  async getRetentionPolicy(): Promise<RetentionPolicy> {
    return { ...this.retentionPolicy };
  }

  async saveRetentionPolicy(policy: RetentionPolicy): Promise<void> {
    this.retentionPolicy = { ...policy };
    this.persistRetention();
  }

  async listRestoreHistory(clientId?: string): Promise<readonly RestoreHistoryEntry[]> {
    return this.restoreHistory
      .filter(h => !clientId || !h.sourceBackupId || true)
      .sort((a, b) => b.restoredAt.localeCompare(a.restoredAt));
  }

  async addRestoreHistory(entry: RestoreHistoryEntry): Promise<void> {
    this.restoreHistory.unshift(entry);
    this.persistHistory();
  }
}
